import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/services/api";
import {
  ArrowLeft, PenTool, Clock, FileText, Send,
  CheckCircle, Star, AlertTriangle, Loader2, BookOpen,
  Mail, FileEdit, ClipboardList
} from "lucide-react";

const TYPE_ICONS = { essay: FileText, letter: Mail, email: Mail, report: ClipboardList };
const TYPE_COLORS = {
  essay: "from-blue-500 to-indigo-500",
  letter: "from-emerald-500 to-teal-500",
  email: "from-violet-500 to-purple-500",
  report: "from-amber-500 to-orange-500",
};

const BankingDescriptivePaperPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { toast } = useToast();

  const [phase, setPhase] = useState("select");
  const [loading, setLoading] = useState(false);
  const [topics, setTopics] = useState({});

  const [sessionId, setSessionId] = useState(null);
  const [paperType, setPaperType] = useState("");
  const [paperName, setPaperName] = useState("");
  const [topic, setTopic] = useState("");
  const [wordLimit, setWordLimit] = useState("");
  const [maxMarks, setMaxMarks] = useState(15);
  const [timeMinutes, setTimeMinutes] = useState(20);

  const [answer, setAnswer] = useState("");
  const [timeLeft, setTimeLeft] = useState(0);
  const timerRef = useRef(null);

  const [results, setResults] = useState(null);

  useEffect(() => { loadTopics(); }, []);

  useEffect(() => {
    if (phase === "write" && timeLeft > 0) {
      timerRef.current = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) { clearInterval(timerRef.current); handleSubmit(); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [phase]);

  const loadTopics = async () => {
    try {
      const res = await fetch(`${API_BASE}/banking/mock-tests/descriptive-topics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setTopics(data.types || {});
    } catch { toast({ variant: "destructive", description: "Failed to load topics" }); }
  };

  const startPaper = async (type) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/banking/mock-tests/descriptive-start?paper_type=${type}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setSessionId(data.session_id);
      setPaperType(data.paper_type);
      setPaperName(data.paper_name);
      setTopic(data.topic);
      setWordLimit(data.word_limit);
      setMaxMarks(data.marks);
      setTimeMinutes(data.time_minutes);
      setTimeLeft(data.time_minutes * 60);
      setAnswer("");
      setPhase("write");
    } catch { toast({ variant: "destructive", description: "Failed to start" }); }
    finally { setLoading(false); }
  };

  const handleSubmit = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (!answer.trim()) {
      toast({ variant: "destructive", description: "Please write something before submitting" });
      return;
    }
    setLoading(true);
    try {
      const timeTaken = timeMinutes * 60 - timeLeft;
      const res = await fetch(`${API_BASE}/banking/mock-tests/descriptive-submit/${sessionId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ answer, time_taken: timeTaken })
      });
      const data = await res.json();
      setResults(data);
      setPhase("results");
    } catch { toast({ variant: "destructive", description: "Failed to submit" }); }
    finally { setLoading(false); }
  };

  const formatTime = (secs) => `${Math.floor(secs / 60).toString().padStart(2, '0')}:${(secs % 60).toString().padStart(2, '0')}`;
  const wordCount = answer.trim() ? answer.trim().split(/\s+/).length : 0;

  // ─── SELECT ────────────────────────────────────────────────────
  if (phase === "select") {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => navigate("/banking")} data-testid="desc-back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-2xl font-semibold text-slate-900">Descriptive Paper Practice</h1>
              <p className="text-sm text-slate-500">Essay, letter, email & report writing for PO/SO Mains</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
            <PenTool className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium text-blue-800">AI-Powered Evaluation</p>
              <p className="text-sm text-blue-700">Your answer will be evaluated by AI for grammar, structure, content relevance, and more. You'll get detailed feedback with model openings and closings.</p>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            {Object.entries(topics).map(([type, data]) => {
              const Icon = TYPE_ICONS[type] || FileText;
              const color = TYPE_COLORS[type] || "from-slate-500 to-slate-600";
              return (
                <Card key={type} className="hover:shadow-lg transition-all border-slate-200 hover:border-blue-300" data-testid={`desc-type-${type}`}>
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shrink-0`}>
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg text-slate-900">{data.name}</h3>
                        <div className="flex items-center gap-3 text-sm text-slate-500 mt-1">
                          <span>{data.marks} marks</span>
                          <span>{data.time_minutes} min</span>
                          <span>{data.word_limit}</span>
                        </div>
                        <p className="text-xs text-slate-400 mt-1">{data.topic_count} topics available</p>
                      </div>
                    </div>
                    <Button
                      className="w-full mt-4 bg-slate-800 hover:bg-slate-900"
                      onClick={() => startPaper(type)}
                      disabled={loading}
                      data-testid={`start-desc-${type}`}
                    >
                      {loading ? "Loading..." : "Start Practice"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ─── WRITE ─────────────────────────────────────────────────────
  if (phase === "write") {
    const isLowTime = timeLeft < 120;
    return (
      <div className="min-h-screen bg-slate-50">
        <div className={`sticky top-0 z-50 px-4 py-3 flex items-center justify-between border-b ${isLowTime ? 'bg-red-50 border-red-200' : 'bg-white border-slate-200'}`}>
          <Badge variant="outline">{paperName}</Badge>
          <div className={`font-mono text-lg font-bold flex items-center gap-2 ${isLowTime ? 'text-red-600 animate-pulse' : 'text-slate-800'}`}>
            <Clock className="w-5 h-5" />
            {formatTime(timeLeft)}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500">{wordCount} words</span>
            <Button size="sm" onClick={handleSubmit} disabled={loading || !answer.trim()} data-testid="submit-desc-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Submit
            </Button>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-6 space-y-4">
          <Card className="border-blue-200 bg-blue-50/50">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-2">
                <Badge className="bg-blue-100 text-blue-700">{paperName}</Badge>
                <Badge variant="outline">{maxMarks} marks</Badge>
                <Badge variant="outline">{wordLimit}</Badge>
              </div>
              <p className="text-lg font-medium text-slate-900 mt-2">{topic}</p>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-0">
              <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder={`Start writing your ${paperType} here...`}
                className="w-full min-h-[400px] p-6 text-base leading-relaxed resize-none focus:outline-none rounded-xl"
                data-testid="desc-answer-textarea"
                autoFocus
              />
            </CardContent>
          </Card>

          <div className="flex items-center justify-between text-sm text-slate-500">
            <span>Word count: <strong className={wordCount < 50 ? 'text-red-600' : 'text-slate-900'}>{wordCount}</strong></span>
            <span>Target: {wordLimit}</span>
          </div>
        </div>
      </div>
    );
  }

  // ─── RESULTS ───────────────────────────────────────────────────
  if (phase === "results" && results) {
    const scorePercent = results.max_score > 0 ? Math.round((results.score / results.max_score) * 100) : 0;
    const scoreColor = scorePercent >= 70 ? "text-green-600" : scorePercent >= 50 ? "text-amber-600" : "text-red-600";

    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-6 py-10 space-y-6">
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={() => { setPhase("select"); setResults(null); }}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-2xl font-semibold text-slate-900">Evaluation Results</h1>
          </div>

          {/* Score Card */}
          <Card className="border-2 border-blue-200">
            <CardContent className="p-8 text-center">
              <p className={`text-5xl font-bold ${scoreColor}`}>{results.score}/{results.max_score}</p>
              <p className="text-sm text-slate-500 mt-1">{results.word_count} words | {Math.floor(results.time_taken / 60)} min</p>

              {/* Sub-scores */}
              {results.grammar_score && (
                <div className="grid grid-cols-4 gap-4 mt-6">
                  {[
                    { label: "Grammar", score: results.grammar_score },
                    { label: "Structure", score: results.structure_score },
                    { label: "Content", score: results.content_score },
                    { label: "Relevance", score: results.relevance_score },
                  ].map((s) => (
                    <div key={s.label} className="bg-slate-50 rounded-xl p-3">
                      <p className="text-2xl font-bold text-slate-800">{s.score}<span className="text-sm text-slate-400">/5</span></p>
                      <p className="text-xs text-slate-500">{s.label}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Feedback */}
          {results.feedback?.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Feedback</h3>
                <ul className="space-y-2">
                  {results.feedback.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                      <span className="w-5 h-5 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center text-xs shrink-0 mt-0.5">{i + 1}</span>
                      {f}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Strengths & Improvements */}
          <div className="grid md:grid-cols-2 gap-4">
            {results.strengths?.length > 0 && (
              <Card className="border-green-200">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-green-700 mb-3 flex items-center gap-2"><CheckCircle className="w-4 h-4" /> Strengths</h3>
                  <ul className="space-y-2">
                    {results.strengths.map((s, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                        <Star className="w-3 h-3 text-green-500 mt-1 shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
            {results.improvements?.length > 0 && (
              <Card className="border-amber-200">
                <CardContent className="p-5">
                  <h3 className="font-semibold text-amber-700 mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Improvements</h3>
                  <ul className="space-y-2">
                    {results.improvements.map((s, i) => (
                      <li key={i} className="text-sm text-slate-700 flex items-start gap-2">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Model Opening & Closing */}
          {(results.model_opening || results.model_closing) && (
            <Card className="bg-slate-50">
              <CardContent className="p-5 space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2"><BookOpen className="w-4 h-4" /> Model Suggestions</h3>
                {results.model_opening && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Better Opening</p>
                    <p className="text-sm text-slate-700 italic bg-white p-3 rounded-lg border border-slate-200">"{results.model_opening}"</p>
                  </div>
                )}
                {results.model_closing && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase font-semibold mb-1">Better Closing</p>
                    <p className="text-sm text-slate-700 italic bg-white p-3 rounded-lg border border-slate-200">"{results.model_closing}"</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <div className="flex gap-3">
            <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => { setPhase("select"); setResults(null); setAnswer(""); }}>
              Practice Another
            </Button>
            <Button variant="outline" onClick={() => navigate("/banking")}>Dashboard</Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default BankingDescriptivePaperPage;
